import { useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  FormControlLabel,
  IconButton,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  LinearProgress,
  MenuItem,
  Paper,
  Skeleton,
  Snackbar,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import CloseIcon from '@mui/icons-material/Close';
import ViewListIcon from '@mui/icons-material/ViewList';
import GridViewIcon from '@mui/icons-material/GridView';
import { TrackProgressBar } from '../TrackProgressBar';
import { DailyLogTimeline } from '../DailyLogTimeline';
import { ProgressChart } from '../ProgressChart';
import type { DailyLog, ProjectTrack } from '../../api/types';
import { formatDate } from '../../lib/format';
import {
  useAuthedPhotoSrc,
  useCreateDailyLog,
  useDailyLogs,
  useProjectDashboard,
  useUploadLogPhoto,
} from '../../hooks/useOps';
import type { TrackItem } from '../../api/types';

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

/** A sub-track's progress %, preferring the backend roll-up value. */
function pctOf(i: { progressPct?: number; currentUnits: number; totalUnits: number }): number {
  return i.progressPct ?? (i.totalUnits > 0 ? Math.round((i.currentUnits / i.totalUnits) * 100) : 0);
}

/** A photo picked in the form but not yet uploaded. */
interface PendingPhoto {
  file: File;
  preview: string;
  caption: string;
}

/**
 * Daily progress: the web entry form (same backend path as the Telegram bot)
 * plus per-track progress with items and the log timeline.
 */
export function ProgressTab({ projectId }: { projectId: string }) {
  const { data } = useProjectDashboard(projectId);
  const createLog = useCreateDailyLog(projectId);

  const [form, setForm] = useState({
    trackId: '',
    trackItemId: '',
    progressIncrement: '',
    reportedDate: todayIso(),
    isBlocked: false,
    note: '',
  });
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [okMsg, setOkMsg] = useState<string | null>(null);

  // Photos picked for the NEXT log entry (W1). Uploaded sequentially after the
  // log is created; a failed photo never blocks or rolls back the log itself.
  const [pendingPhotos, setPendingPhotos] = useState<PendingPhoto[]>([]);
  const [retryUpload, setRetryUpload] = useState<{ logId: string; trackId: string; photos: PendingPhoto[] } | null>(
    null,
  );
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadPhoto = useUploadLogPhoto();

  if (!data) return null;
  const { tracks } = data;
  const selectedTrack = tracks.find((t) => t.id === form.trackId);
  const selectedItem = selectedTrack?.items?.find((i) => i.id === form.trackItemId);

  // Two modes. Pick a sub-track → you enter the ABSOLUTE % it's now at, and the
  // log records the delta. A track with NO sub-tracks → you enter track-unit
  // increments. A track that HAS sub-tracks must be logged through one of them
  // (its % rolls up from the sub-tracks), so whole-track logging is blocked there.
  const itemMode = Boolean(selectedItem);
  const hasItems = Boolean(selectedTrack && (selectedTrack.items ?? []).length > 0);
  const needsSubtrack = hasItems && !itemMode && !form.isBlocked;
  const itemCurrentPct = selectedItem ? pctOf(selectedItem) : 0;

  const entered = parseInt(form.progressIncrement, 10);
  const enteredValid = Number.isInteger(entered);

  const remaining =
    selectedTrack && !itemMode
      ? Math.max(0, selectedTrack.totalUnits - selectedTrack.currentUnits)
      : null;

  // What we send to the backend: the sub-track delta (newPct − currentPct), or
  // the track-unit increment when whole-track.
  const increment = itemMode
    ? enteredValid
      ? entered - itemCurrentPct
      : 0
    : enteredValid
      ? entered
      : 0;

  const itemOutOfRange = itemMode && enteredValid && (entered < 0 || entered > 100);
  const itemNotForward = itemMode && enteredValid && entered <= itemCurrentPct;
  const overRemaining = !itemMode && !form.isBlocked && remaining !== null && increment > remaining;

  const canSubmit =
    Boolean(form.trackId) &&
    Boolean(form.reportedDate) &&
    (form.isBlocked
      ? true
      : needsSubtrack
        ? false
        : itemMode
          ? enteredValid && !itemOutOfRange && !itemNotForward
          : increment > 0 && !overRemaining);

  const addPhotos = (files: FileList | null) => {
    if (!files) return;
    const next = Array.from(files).map((file) => ({
      file,
      preview: URL.createObjectURL(file),
      caption: '',
    }));
    setPendingPhotos((p) => [...p, ...next]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removePending = (index: number) => {
    setPendingPhotos((p) => {
      URL.revokeObjectURL(p[index].preview);
      return p.filter((_, i) => i !== index);
    });
  };

  /** Sequential upload; returns the photos that failed. */
  const uploadAll = async (logId: string, trackId: string, photos: PendingPhoto[]): Promise<PendingPhoto[]> => {
    const failed: PendingPhoto[] = [];
    for (const photo of photos) {
      try {
        await uploadPhoto.mutateAsync({ logId, trackId, file: photo.file, caption: photo.caption });
        URL.revokeObjectURL(photo.preview);
      } catch {
        failed.push(photo);
      }
    }
    return failed;
  };

  const submit = async () => {
    try {
      const saved = await createLog.mutateAsync({
        trackId: form.trackId,
        trackItemId: form.trackItemId || undefined,
        reportedDate: form.reportedDate,
        progressIncrement: form.isBlocked ? 0 : increment,
        isBlocked: form.isBlocked,
        note: form.note.trim() || undefined,
      });

      const failed = await uploadAll(saved.id, saved.trackId, pendingPhotos);
      setPendingPhotos([]);
      if (failed.length) {
        setRetryUpload({ logId: saved.id, trackId: saved.trackId, photos: failed });
        setErrorMsg(`Log saved, but ${failed.length} photo(s) failed to upload — retry below.`);
      } else {
        setOkMsg(
          form.isBlocked
            ? 'Blocked day recorded.'
            : itemMode
              ? `${selectedItem?.name} set to ${entered}%.`
              : `+${increment} ${selectedTrack?.unitLabel ?? 'units'} logged.`,
        );
      }
      setForm((f) => ({ ...f, progressIncrement: '', note: '', isBlocked: false }));
    } catch (e) {
      setErrorMsg((e as Error).message ?? 'Failed to record the log');
    }
  };

  const retryFailed = async () => {
    if (!retryUpload) return;
    const failed = await uploadAll(retryUpload.logId, retryUpload.trackId, retryUpload.photos);
    if (failed.length) {
      setRetryUpload({ ...retryUpload, photos: failed });
      setErrorMsg(`${failed.length} photo(s) still failing — check the backend and retry.`);
    } else {
      setRetryUpload(null);
      setOkMsg('All photos uploaded.');
    }
  };

  return (
    <Stack gap={2}>
      {/* Entry form */}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle1" fontWeight={600} mb={1.5}>
          Log progress
        </Typography>
        {tracks.length === 0 ? (
          <Typography color="text.secondary" variant="body2">
            No tracks yet — create them in the Plan tab first.
          </Typography>
        ) : (
          <Stack direction="row" gap={1} flexWrap="wrap" alignItems="flex-start">
            <TextField
              select
              size="small"
              label="Track"
              value={form.trackId}
              onChange={(e) =>
                setForm((f) => ({ ...f, trackId: e.target.value, trackItemId: '', progressIncrement: '' }))
              }
              sx={{ minWidth: 200 }}
            >
              {tracks.map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  {t.label} ({t.progressPct ?? 0}%)
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              label="Sub-track"
              value={form.trackItemId}
              // Picking a sub-track switches the amount field to a % and prefills
              // it with where the sub-track stands now.
              onChange={(e) => {
                const it = selectedTrack?.items?.find((i) => i.id === e.target.value);
                setForm((f) => ({
                  ...f,
                  trackItemId: e.target.value,
                  progressIncrement: it ? String(pctOf(it)) : '',
                }));
              }}
              sx={{ minWidth: 200 }}
              disabled={!selectedTrack || (selectedTrack.items ?? []).length === 0}
            >
              <MenuItem value="">{hasItems ? '— select —' : '(whole track)'}</MenuItem>
              {[...(selectedTrack?.items ?? [])]
                .sort((a, b) => a.sequence - b.sequence)
                .map((i) => (
                  <MenuItem key={i.id} value={i.id}>
                    {i.sequence}. {i.name} — {pctOf(i)}%
                  </MenuItem>
                ))}
            </TextField>
            <TextField
              size="small"
              type="number"
              label={itemMode ? '% complete' : `Units${selectedTrack ? ` (${selectedTrack.unitLabel})` : ''}`}
              value={form.isBlocked ? '0' : form.progressIncrement}
              onChange={(e) => setForm((f) => ({ ...f, progressIncrement: e.target.value }))}
              disabled={form.isBlocked || needsSubtrack}
              error={overRemaining || itemOutOfRange || itemNotForward}
              helperText={
                needsSubtrack
                  ? 'pick a sub-track to log its %'
                  : itemMode
                    ? itemOutOfRange
                      ? '0–100 only'
                      : itemNotForward
                        ? `must be above current ${itemCurrentPct}%`
                        : `now ${itemCurrentPct}% — enter the new %`
                    : overRemaining
                      ? `only ${remaining} ${selectedTrack?.unitLabel ?? ''} remaining`
                      : remaining !== null
                        ? `${remaining} remaining`
                        : undefined
              }
              inputProps={itemMode ? { min: 0, max: 100 } : undefined}
              sx={{ width: 170 }}
            />
            <TextField
              size="small"
              type="date"
              label="Date"
              value={form.reportedDate}
              onChange={(e) => setForm((f) => ({ ...f, reportedDate: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 160 }}
            />
            <TextField
              size="small"
              label="Note (optional)"
              value={form.note}
              onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
              sx={{ minWidth: 220, flex: 1 }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={form.isBlocked}
                  onChange={(e) => setForm((f) => ({ ...f, isBlocked: e.target.checked }))}
                />
              }
              label="Blocked (no progress)"
            />
            <Button
              size="small"
              startIcon={<AddPhotoAlternateIcon />}
              onClick={() => fileInputRef.current?.click()}
              sx={{ alignSelf: 'center' }}
            >
              Photos{pendingPhotos.length ? ` (${pendingPhotos.length})` : ''}
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={submit}
              disabled={!canSubmit || createLog.isPending || uploadPhoto.isPending}
              sx={{ alignSelf: 'center' }}
            >
              Log
            </Button>
          </Stack>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          multiple
          hidden
          data-testid="photo-input"
          onChange={(e) => addPhotos(e.target.files)}
        />

        {/* pending photo previews with per-photo caption */}
        {pendingPhotos.length > 0 && (
          <Stack direction="row" gap={1.5} mt={1.5} flexWrap="wrap">
            {pendingPhotos.map((p, i) => (
              <Stack key={p.preview} gap={0.5} sx={{ width: 120 }}>
                <Box sx={{ position: 'relative' }}>
                  <Box
                    component="img"
                    src={p.preview}
                    alt={p.file.name}
                    sx={{ width: 120, height: 90, objectFit: 'cover', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => removePending(i)}
                    sx={{ position: 'absolute', top: 2, right: 2, bgcolor: 'background.paper', '&:hover': { bgcolor: 'background.paper' } }}
                  >
                    <CloseIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Box>
                <TextField
                  size="small"
                  placeholder="Caption"
                  value={p.caption}
                  inputProps={{ maxLength: 500 }}
                  onChange={(e) =>
                    setPendingPhotos((all) => all.map((x, j) => (j === i ? { ...x, caption: e.target.value } : x)))
                  }
                />
              </Stack>
            ))}
          </Stack>
        )}

        {/* photos that failed after the log was saved — log is safe, retry uploads only */}
        {retryUpload && (
          <Alert
            severity="warning"
            sx={{ mt: 1.5 }}
            action={
              <Stack direction="row" gap={1}>
                <Button size="small" color="warning" onClick={retryFailed} disabled={uploadPhoto.isPending}>
                  Retry upload
                </Button>
                <Button size="small" onClick={() => setRetryUpload(null)}>
                  Discard
                </Button>
              </Stack>
            }
          >
            The log entry was saved, but {retryUpload.photos.length} photo(s) did not upload.
          </Alert>
        )}
      </Paper>

      {/* Per-track progress + timeline */}
      {tracks.map((track) => (
        <Accordion key={track.id} disableGutters variant="outlined">
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack sx={{ width: '100%' }} pr={1}>
              <TrackProgressBar track={track} />
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            {(track.items ?? []).length > 0 && (
              <Stack gap={1} mb={2}>
                {[...(track.items ?? [])]
                  .sort((a, b) => a.sequence - b.sequence)
                  .map((item) => (
                    <SubTaskProgressRow key={item.id} item={item} />
                  ))}
              </Stack>
            )}
            <TrackLogs track={track} />
          </AccordionDetails>
        </Accordion>
      ))}

      <Snackbar
        open={Boolean(errorMsg)}
        autoHideDuration={6000}
        onClose={() => setErrorMsg(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setErrorMsg(null)} sx={{ width: '100%' }}>
          {errorMsg}
        </Alert>
      </Snackbar>
      <Snackbar
        open={Boolean(okMsg)}
        autoHideDuration={3000}
        onClose={() => setOkMsg(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setOkMsg(null)} sx={{ width: '100%' }}>
          {okMsg}
        </Alert>
      </Snackbar>
    </Stack>
  );
}

/**
 * One sub-track row: name + progress bar + % (read-only). Progress is entered
 * through the "Log progress" form above — picking the sub-track there — so every
 * change is a dated history entry rather than a silent edit.
 */
function SubTaskProgressRow({ item }: { item: TrackItem }) {
  const pct = pctOf(item);
  return (
    <Stack direction="row" gap={1.5} alignItems="center">
      <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }}>
        {item.sequence}. {item.name}
      </Typography>
      <LinearProgress
        variant="determinate"
        value={pct}
        color={pct === 100 ? 'success' : 'primary'}
        sx={{ width: 120, height: 6, borderRadius: 3 }}
      />
      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 34, textAlign: 'right' }}>
        {pct}%
      </Typography>
    </Stack>
  );
}

/** Lazy per-track chart + list/gallery — only fetches when the accordion has rendered it. */
function TrackLogs({ track }: { track: ProjectTrack }) {
  const { data: logs, isLoading } = useDailyLogs(track.id);
  const [view, setView] = useState<'list' | 'gallery'>('list');
  const photoCount = (logs ?? []).reduce((n, l) => n + (l.photos?.length ?? 0), 0);

  // Sub-track logs are recorded in %, so scale the chart to the roll-up (total =
  // Σ sub-track targets, current = Σ done) — otherwise the cumulative line would
  // overshoot the track's own unit total. Item-less tracks chart as-is.
  const items = track.items ?? [];
  const chartTrack: ProjectTrack = items.length
    ? {
        ...track,
        totalUnits: items.reduce((s, i) => s + i.totalUnits, 0),
        currentUnits: items.reduce((s, i) => s + i.currentUnits, 0),
      }
    : track;
  const logUnitLabel = items.length ? '%' : track.unitLabel;

  return (
    <Stack gap={2}>
      {!isLoading && <ProgressChart track={chartTrack} logs={logs ?? []} />}
      {photoCount > 0 && (
        <ToggleButtonGroup
          size="small"
          exclusive
          value={view}
          onChange={(_e, v: 'list' | 'gallery' | null) => v && setView(v)}
          sx={{ alignSelf: 'flex-start' }}
        >
          <ToggleButton value="list">
            <ViewListIcon fontSize="small" sx={{ mr: 0.5 }} /> Log
          </ToggleButton>
          <ToggleButton value="gallery">
            <GridViewIcon fontSize="small" sx={{ mr: 0.5 }} /> Photos ({photoCount})
          </ToggleButton>
        </ToggleButtonGroup>
      )}
      {view === 'list' ? (
        <DailyLogTimeline logs={logs ?? []} isLoading={isLoading} unitLabel={logUnitLabel} canEdit />
      ) : (
        <TrackPhotoGallery logs={logs ?? []} />
      )}
    </Stack>
  );
}

/**
 * Per-track photo gallery (W1): every photo across the track's logs, newest
 * first, caption + log date overlaid, lightbox on click. Pure client-side —
 * photos arrive joined on the daily-logs query.
 */
function TrackPhotoGallery({ logs }: { logs: DailyLog[] }) {
  const [lightbox, setLightbox] = useState<{ src: string; caption: string | null } | null>(null);

  const photos = logs.flatMap((log) =>
    (log.photos ?? []).map((p) => ({ ...p, reportedDate: log.reportedDate })),
  );
  // logs already arrive newest-first; photos inherit that order.

  if (!photos.length) {
    return (
      <Typography color="text.secondary" variant="body2">
        No photos on this track yet.
      </Typography>
    );
  }

  return (
    <>
      <ImageList cols={4} gap={8} sx={{ m: 0 }}>
        {photos.map((p) => (
          <GalleryPhoto
            key={p.id}
            filePath={p.filePath}
            caption={p.caption ?? null}
            reportedDate={p.reportedDate}
            onOpen={(src) => setLightbox({ src, caption: p.caption ?? null })}
          />
        ))}
      </ImageList>

      <Dialog open={Boolean(lightbox)} onClose={() => setLightbox(null)} maxWidth="md">
        <DialogContent sx={{ p: 0 }}>
          {lightbox && (
            <Box component="img" src={lightbox.src} alt={lightbox.caption ?? 'full size'} sx={{ maxWidth: '90vw', maxHeight: '75vh', display: 'block' }} />
          )}
        </DialogContent>
        {lightbox?.caption && (
          <DialogActions sx={{ px: 2, pb: 1.5, justifyContent: 'flex-start' }}>
            <Typography variant="body2" color="text.secondary">{lightbox.caption}</Typography>
          </DialogActions>
        )}
      </Dialog>
    </>
  );
}

/**
 * One gallery tile. Fetches the photo with the bearer token and renders a blob
 * object-URL — a raw /api/photos URL in an <img src> 401s (auth-guarded route,
 * images can't carry the Authorization header). Own component so the hook runs
 * once per photo (rules of hooks).
 */
function GalleryPhoto({
  filePath,
  caption,
  reportedDate,
  onOpen,
}: {
  filePath: string;
  caption: string | null;
  reportedDate: string;
  onOpen: (src: string) => void;
}) {
  const src = useAuthedPhotoSrc(filePath);
  return (
    <ImageListItem
      sx={{ cursor: src ? 'pointer' : 'default', borderRadius: 1, overflow: 'hidden' }}
      onClick={() => src && onOpen(src)}
    >
      {src ? (
        <Box
          component="img"
          src={src}
          alt={caption ?? 'progress'}
          loading="lazy"
          sx={{ width: '100%', height: 140, objectFit: 'cover' }}
        />
      ) : (
        <Skeleton variant="rectangular" sx={{ width: '100%', height: 140 }} />
      )}
      <ImageListItemBar
        title={caption ?? ''}
        subtitle={formatDate(reportedDate)}
        sx={{ '& .MuiImageListItemBar-title': { fontSize: 12 }, '& .MuiImageListItemBar-subtitle': { fontSize: 10 } }}
      />
    </ImageListItem>
  );
}
